/**
 * Landing Page Component
 * 
 * Public landing page component displayed to unauthenticated users and as a
 * redirect destination for authenticated users. This component serves as the
 * main entry point to the MacSafety E-Forms application, providing different
 * interfaces based on user authentication status.
 * 
 * Key Features:
 * - Public welcome interface for unauthenticated users
 * - Authentication status detection using reactive signals
 * - Conditional UI rendering based on login state
 * - Navigation to authenticated areas of the application
 * - Responsive design with centered content layout
 * 
 * User States:
 * - Unauthenticated: Welcome message with login button
 * - Authenticated: Welcome back message with navigation options
 * - Admin Users: Additional access to form entries management
 * 
 * Authentication Flow:
 * - Login button triggers Microsoft Entra ID authentication
 * - Automatic UI updates when authentication state changes
 * - Reactive navigation options based on user permissions
 * 
 * Navigation Options:
 * - Forms Landing: Access to form submission (all authenticated users)
 * - Form Entries: Access to form management (admin users only)
 * - Login Process: Microsoft Entra ID authentication flow
 * 
 * Service Dependencies:
 * - EntraService: Authentication state and user information
 * - Router: Navigation to authenticated application areas
 * 
 * Responsive Design:
 * - Centered grid layout that adapts to different screen sizes
 * - Content columns that adjust from full width to centered layout
 * - Consistent spacing and typography across all devices
 * 
 * @author MacSafety Development Team
 * @version 1.0
 */

import { Component, computed } from '@angular/core';
import { Router } from '@angular/router';
import { EntraService } from '../../../services/entra.service';

@Component({
  selector: 'app-landing-page',
  templateUrl: './landing-page.component.html',
  styleUrls: ['./landing-page.component.css']
})
export class LandingPageComponent {
  // Signal-based properties for reactive authentication state
  // These automatically update when the underlying service state changes
  public readonly isLoggedIn = computed(() => this.entraService.userIsLoggedIn());
  public readonly userName = computed(() => this.entraService.userName());
  public readonly isAdmin = computed(() => this.entraService.isAdmin());

  constructor(
    private router: Router,
    private entraService: EntraService
  ) {}

  /**
   * Initiates the Microsoft Entra ID authentication process
   * Triggers the login flow through the EntraService
   * Redirects user to Microsoft authentication when called
   */
  login(): void {
    this.entraService.login();
  }

  /**
   * Navigates to the forms landing page for authenticated users
   * Routes to the main forms interface where users can submit new forms
   * Only accessible after successful authentication
   */
  goToForms(): void {
    this.router.navigate(['/forms']);
  }

  /**
   * Navigates to the form entries management page
   * Routes to the form entries interface for browsing and managing submissions
   * Only accessible to authenticated admin users
   */
  goToFormEntries(): void {
    this.router.navigate(['/form-entries']);
  }
} 